import DOMPurify from 'dompurify';
import React, { useState } from 'react';

import { UserPreference } from '@ndvr/customer-api';

import { MarkdownTiptapEditor } from '@/components/minimal-tiptap';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/utils/cn';
import { md, PROSE_CLASSES } from '@/views/ai-chat/RenderedMarkdown';
import {
    useChatMemory,
    useDeleteChatMemory,
    useUpdateChatMemory,
    useUpdateUserPreference,
    useUpdateWritingStyle,
    useUserPreferences,
    useWritingStyle,
} from '@/views/preferences/user-preference-queries';

export const UserPreferences = () => {
    const { data: preferences = [], isLoading: preferencesLoading } = useUserPreferences();
    const { mutate: updatePreference, isPending: preferencePending } = useUpdateUserPreference();
    const { data: writingStyle, isLoading: writingStyleLoading } = useWritingStyle();
    const { mutate: updateWritingStyle, isPending: updatePending } = useUpdateWritingStyle();
    const { data: chatMemory, isLoading: chatMemoryLoading } = useChatMemory();
    const { mutate: updateChatMemory, isPending: memoryUpdatePending } = useUpdateChatMemory();
    const { mutate: deleteChatMemory, isPending: memoryDeletePending } = useDeleteChatMemory();
    const [isEditing, setIsEditing] = useState(false);
    const [editedStyle, setEditedStyle] = useState('');
    const [isEditingMemory, setIsEditingMemory] = useState(false);
    const [editedMemory, setEditedMemory] = useState('');

    if (preferencesLoading || writingStyleLoading || chatMemoryLoading) {
        return (
            <div className="flex items-center justify-center p-12 text-sm text-gray-500">Loading preferences...</div>
        );
    }

    const meetingBotPreferences = preferences.filter(
        ({ preference }) => preference === UserPreference.AUTO_SCHEDULE_MEETING_BOT,
    );

    const handleSave = () => {
        updateWritingStyle(editedStyle);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditedStyle(writingStyle?.styleDescription || '');
        setIsEditing(false);
    };

    const handleEdit = () => {
        setEditedStyle(writingStyle?.styleDescription || '');
        setIsEditing(true);
    };

    const memoryPending = memoryUpdatePending || memoryDeletePending;

    const handleMemorySave = () => {
        updateChatMemory(editedMemory);
        setIsEditingMemory(false);
    };

    const handleMemoryCancel = () => {
        setEditedMemory(chatMemory?.content || '');
        setIsEditingMemory(false);
    };

    const handleMemoryEdit = () => {
        setEditedMemory(chatMemory?.content || '');
        setIsEditingMemory(true);
    };

    const handleMemoryClear = () => {
        if (window.confirm('Clear all saved memory?')) {
            deleteChatMemory();
        }
    };

    return (
        <div className="flex flex-col gap-6 p-8">
            <Card>
                <CardHeader>
                    <CardTitle className="py-2">Claire&#8482;</CardTitle>
                </CardHeader>
                <CardContent className="divide-y divide-gray-100">
                    {meetingBotPreferences.map(({ preference, description, enabled }) => (
                        <div key={preference} className="flex items-center justify-between gap-6 py-4">
                            <span className="text-sm">{description}</span>
                            <Switch
                                checked={enabled}
                                disabled={preferencePending}
                                onCheckedChange={(checked) => updatePreference({ preference, enabled: checked })}
                            />
                        </div>
                    ))}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="py-2">Writing Style</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {isEditing ? (
                        <div className="space-y-4">
                            <MarkdownTiptapEditor
                                value={editedStyle}
                                onChange={setEditedStyle}
                                placeholder="Describe your writing style..."
                                editorContentClassName="max-h-[70vh] min-h-96 overflow-y-auto p-4"
                            />
                            <div className="flex gap-2">
                                <Button
                                    onClick={handleSave}
                                    disabled={updatePending || !editedStyle.trim() || editedStyle.length > 10000}
                                >
                                    Save
                                </Button>
                                <Button variant="outline" onClick={handleCancel} disabled={updatePending}>
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {writingStyle?.styleDescription ? (
                                <div
                                    className={cn(PROSE_CLASSES, 'text-gray-700')}
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(md.render(writingStyle.styleDescription)),
                                    }}
                                />
                            ) : (
                                <div className="text-sm text-gray-500">No writing style profile set.</div>
                            )}
                            <Button variant="outline" onClick={handleEdit} disabled={updatePending}>
                                {writingStyle?.styleDescription ? 'Edit' : 'Add Writing Style'}
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="py-2">Memory</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {isEditingMemory ? (
                        <div className="space-y-4">
                            <MarkdownTiptapEditor
                                value={editedMemory}
                                onChange={setEditedMemory}
                                placeholder="Durable preferences and facts Claire should remember..."
                                editorContentClassName="max-h-[70vh] min-h-96 overflow-y-auto p-4"
                            />
                            <div className="flex gap-2">
                                <Button
                                    onClick={handleMemorySave}
                                    disabled={memoryPending || !editedMemory.trim() || editedMemory.length > 10000}
                                >
                                    Save
                                </Button>
                                <Button variant="outline" onClick={handleMemoryCancel} disabled={memoryPending}>
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {chatMemory?.content ? (
                                <div
                                    className={cn(PROSE_CLASSES, 'text-gray-700')}
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(md.render(chatMemory.content)),
                                    }}
                                />
                            ) : (
                                <div className="text-sm text-gray-500">
                                    No memory saved yet. Claire saves durable preferences here as you chat, or you can
                                    add them yourself.
                                </div>
                            )}
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={handleMemoryEdit} disabled={memoryPending}>
                                    {chatMemory?.content ? 'Edit' : 'Add Memory'}
                                </Button>
                                {chatMemory?.content && (
                                    <Button variant="outline" onClick={handleMemoryClear} disabled={memoryPending}>
                                        Clear
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
